import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';

const Symbols = () => {
  const [symbols, setSymbols] = useState([]);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    symbol: ''
  });
  const [marketCapFilter, setMarketCapFilter] = useState({
    operator: 'higher',
    value: ''
  });
  const [totalVolumeFilter, setTotalVolumeFilter] = useState({
    operator: 'higher',
    value: ''
  });
  const [sortBy, setSortBy] = useState({
    column: 'market_cap',
    direction: '9-1'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    document.title = 'Symbols';
    fetchSymbols();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, symbols, sortBy, marketCapFilter, totalVolumeFilter]);

  const fetchSymbols = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/symbols`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSymbols(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch symbols');
      }

      setLoading(false);

    } catch (err) {
      console.error('Error fetching symbols:', err);
      setError('Failed to fetch symbols data');
      setSymbols([]);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = symbols;

    // Apply symbol filter
    if (filters.symbol && filters.symbol.trim() !== '') {
      filtered = filtered.filter(symbol =>
        symbol.symbol?.toLowerCase().includes(filters.symbol.toLowerCase()) ||
        symbol.symbol_name?.toLowerCase().includes(filters.symbol.toLowerCase())
      );
    }

    // Apply market cap filter
    if (marketCapFilter.value && !isNaN(Number(marketCapFilter.value))) {
      const filterValue = Number(marketCapFilter.value);
      filtered = filtered.filter(symbol => {
        const marketCap = Number(symbol.market_cap);
        if (isNaN(marketCap)) return false;
        if (marketCapFilter.operator === 'higher') {
          return marketCap > filterValue;
        } else {
          return marketCap < filterValue;
        }
      });
    }

    // Apply total volume filter
    if (totalVolumeFilter.value && !isNaN(Number(totalVolumeFilter.value))) {
      const filterValue = Number(totalVolumeFilter.value);
      filtered = filtered.filter(symbol => {
        const totalVolume = Number(symbol.total_volume);
        if (isNaN(totalVolume)) return false;
        if (totalVolumeFilter.operator === 'higher') {
          return totalVolume > filterValue;
        } else {
          return totalVolume < filterValue;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy.column];
      let bVal = b[sortBy.column];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;

      // Convert to numbers for numeric columns
      if (['market_cap', 'total_volume', 'current_price',
           'circulating_supply', 'total_supply', 'max_supply'].includes(sortBy.column)) {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }

      if (sortBy.direction === '1-9') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    setFilteredSymbols(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ symbol: '' });
    setMarketCapFilter({ operator: 'higher', value: '' });
    setTotalVolumeFilter({ operator: 'higher', value: '' });
    setSortBy({ column: 'market_cap', direction: '9-1' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const formatLastUpdateDate = (dateString) => {
    if (!dateString) return { date: '-', time: '-' };

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return { date: '-', time: '-' };
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    const number = Number(num);

    // Numbers over 1 billion: divide by 1 million and add "M"
    if (number > 1000000000) {
      const value = number / 1000000;
      const formatted = value.toFixed(2);
      // Add spaces as thousand separators
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      // Pad to ensure consistent width (max 7 chars for integer part with spaces: "999 999")
      const intPart = parts[0].padStart(7, '\u00A0'); // non-breaking space
      return intPart + '.' + parts[1] + 'M';
    }

    // Numbers over 1 million: divide by 1000 and add "k"
    if (number > 1000000) {
      const value = number / 1000;
      const formatted = value.toFixed(2);
      // Add spaces as thousand separators
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      // Pad to ensure consistent width (max 7 chars for integer part with spaces: "999 999")
      const intPart = parts[0].padStart(7, '\u00A0'); // non-breaking space
      return intPart + '.' + parts[1] + 'k';
    }

    // Numbers 1000 and below: display with space as thousands delimiter
    const formatted = number.toFixed(2);
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
  };

  const formatMarketCap = (num) => {
    if (num === null || num === undefined) return '-';
    return '$' + formatNumber(num);
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `$${Number(price).toFixed(2)}`;
  };


  if (loading) {
    return (
      <div className="loading">
        Loading symbols data...
      </div>
    );
  }

  // Calculate pagination
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredSymbols.length / itemsPerPage);
  const startIndex = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === 'all' ? filteredSymbols.length : startIndex + itemsPerPage;
  const currentPageData = filteredSymbols.slice(startIndex, endIndex);

  return (
    <div className="page-content">
      {error && (
        <div className="error" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'inline-block' }}>
      <div className="filters" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#1a1b23', border: '1px solid #2a2d3a', borderRadius: '4px', fontFamily: "'Courier New', Courier, monospace" }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Left column - Filter components */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '100px', marginRight: '10px' }}>Symbol:</label>
            <input
              type="text"
              value={filters.symbol}
              onChange={(e) => handleFilterChange('symbol', e.target.value)}
              placeholder="Filter by symbol"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '100px', marginRight: '10px' }}>Market Cap:</label>
            <select
              value={marketCapFilter.operator}
              onChange={(e) => setMarketCapFilter(prev => ({ ...prev, operator: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '90px',
                marginRight: '5px'
              }}
            >
              <option value="higher">Higher than</option>
              <option value="lower">Lower than</option>
            </select>
            <input
              type="text"
              value={marketCapFilter.value}
              onChange={(e) => setMarketCapFilter(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Value"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '105px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '100px', marginRight: '10px' }}>Total Volume:</label>
            <select
              value={totalVolumeFilter.operator}
              onChange={(e) => setTotalVolumeFilter(prev => ({ ...prev, operator: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '90px',
                marginRight: '5px'
              }}
            >
              <option value="higher">Higher than</option>
              <option value="lower">Lower than</option>
            </select>
            <input
              type="text"
              value={totalVolumeFilter.value}
              onChange={(e) => setTotalVolumeFilter(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Value"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '105px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '100px', marginRight: '10px' }}>Sort:</label>
            <select
              value={sortBy.column}
              onChange={(e) => setSortBy(prev => ({ ...prev, column: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '120px',
                marginRight: '5px'
              }}
            >
              <option value="symbol">Symbol</option>
              <option value="current_price">Current Price</option>
              <option value="market_cap">Market Cap</option>
              <option value="total_volume">Total Volume</option>
              <option value="circulating_supply">Circulating Supply</option>
            </select>
            <select
              value={sortBy.direction}
              onChange={(e) => setSortBy(prev => ({ ...prev, direction: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '75px'
              }}
            >
              <option value="1-9">1→9</option>
              <option value="9-1">9→1</option>
            </select>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#f85149',
                border: 'none',
                borderRadius: '3px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
          </div>

          {/* Right column - Refresh button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }} className="hide-mobile">
            <button
              onClick={() => {
                fetchSymbols();
              }}
              disabled={loading}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#238636',
                color: '#ffffff',
                border: '1px solid #238636',
                borderRadius: '3px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s',
                width: '80px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#2ea043';
                  e.target.style.borderColor = '#2ea043';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#238636';
                  e.target.style.borderColor = '#238636';
                }
              }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Records per page selector */}
      <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ color: '#8b949e', fontSize: '12px', whiteSpace: 'nowrap', fontFamily: "'Courier New', Courier, monospace" }}>Records per page:</label>
        <select
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(e.target.value)}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            fontFamily: "'Courier New', Courier, monospace",
            backgroundColor: '#21262d',
            border: '1px solid #2a2d3a',
            borderRadius: '3px',
            color: '#ffffff',
            width: '80px'
          }}
        >
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th></th>
              <th>Symbol</th>
              <th style={{ textAlign: 'right', width: '95px', maxWidth: '95px' }}>Market<br/>Cap</th>
              <th style={{ textAlign: 'center' }}>AVG DTM<br/>0.1%</th>
              <th className="hide-mobile">Status</th>
              <th style={{ textAlign: 'right' }} className="hide-mobile">Current<br/>Price</th>
              <th style={{ textAlign: 'right' }} className="hide-mobile">Total<br/>Volume</th>
              <th className="hide-mobile">Last<br/>Update</th>
              <th style={{ textAlign: 'right' }} className="hide-mobile">Circulating<br/>Supply</th>
              <th style={{ textAlign: 'right' }} className="hide-mobile">Total<br/>Supply</th>
              <th style={{ textAlign: 'right' }} className="hide-mobile">Max<br/>Supply</th>
              <th className="hide-mobile">Name<br/></th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.map((symbol) => (
              <React.Fragment key={symbol.id}>
                <tr
                  onClick={() => toggleRow(symbol.id)}
                  style={{ cursor: 'pointer' }}
                  className="symbol-row"
                >
                  <td style={{
                    color: symbol.status?.toLowerCase() === 'active' ? '#3fb950' :
                           symbol.status?.toLowerCase() === 'inactive' ? '#f85149' : '#ffffff'
                  }}>
                    {symbol.id}
                  </td>
                  <td>
                    {symbol.image_url ? (
                      <img
                        src={symbol.image_url}
                        alt={symbol.symbol || 'Symbol'}
                        title={symbol.image_url}
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'inline';
                        }}
                      />
                    ) : (
                      <span style={{ color: '#8b949e', fontSize: '10px' }}>-</span>
                    )}
                    {symbol.image_url && (
                      <span style={{ display: 'none', color: '#8b949e', fontSize: '10px' }}>
                        No img
                      </span>
                    )}
                  </td>
                  <td title={symbol.symbol_name || symbol.symbol}>
                    <strong>{symbol.symbol?.toUpperCase()}</strong>
                  </td>
                  <td style={{ textAlign: 'right', width: '95px', maxWidth: '95px' }}>{formatMarketCap(symbol.market_cap)}</td>
                  <td style={{ textAlign: 'center', color: '#8b949e' }}>&lt;tbd&gt;</td>
                  <td className="hide-mobile">
                    <span className={`status-badge ${symbol.status?.toLowerCase()}`}>
                      {symbol.status || '-'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }} className="hide-mobile">{formatPrice(symbol.current_price)}</td>
                  <td style={{ textAlign: 'right' }} className="hide-mobile">{formatNumber(symbol.total_volume)}</td>
                  <td style={{ padding: '2px 4px', lineHeight: '1.2' }} className="hide-mobile">
                    <div style={{ color: '#8b949e' }}>
                      {formatLastUpdateDate(symbol.last_update).date}
                    </div>
                    <div style={{ color: '#8b949e' }}>
                      {formatLastUpdateDate(symbol.last_update).time}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }} className="hide-mobile">{formatNumber(symbol.circulating_supply)}</td>
                  <td style={{ textAlign: 'right' }} className="hide-mobile">{formatNumber(symbol.total_supply)}</td>
                  <td style={{ textAlign: 'right' }} className="hide-mobile">{formatNumber(symbol.max_supply)}</td>
                  <td className="hide-mobile">{symbol.coingecko_id || '-'}</td>
                </tr>
                {expandedRow === symbol.id && (
                  <tr className="mobile-details">
                    <td colSpan="13" style={{ padding: '8px', backgroundColor: '#161b22' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
                        <div><strong>AVG DTM 0.1%:</strong> <span style={{ color: '#8b949e' }}>&lt;tbd&gt;</span></div>
                        <div><strong>Status:</strong> <span className={`status-badge ${symbol.status?.toLowerCase()}`}>{symbol.status || '-'}</span></div>
                        <div><strong>Current Price:</strong> {formatPrice(symbol.current_price)}</div>
                        <div><strong>Total Volume:</strong> {formatNumber(symbol.total_volume)}</div>
                        <div><strong>Last Update:</strong> {formatLastUpdateDate(symbol.last_update).date} {formatLastUpdateDate(symbol.last_update).time}</div>
                        <div><strong>Circulating Supply:</strong> {formatNumber(symbol.circulating_supply)}</div>
                        <div><strong>Total Supply:</strong> {formatNumber(symbol.total_supply)}</div>
                        <div><strong>Max Supply:</strong> {formatNumber(symbol.max_supply)}</div>
                        <div><strong>Name:</strong> {symbol.coingecko_id || '-'}</div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ color: '#8b949e', fontSize: '10px', fontFamily: "'Courier New', Courier, monospace", textAlign: 'center' }}>
          Showing: {startIndex + 1}-{Math.min(endIndex, filteredSymbols.length)} of {filteredSymbols.length} filtered records ({symbols.length} total)
        </div>
        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div style={{ color: '#8b949e', fontSize: '10px', fontFamily: "'Courier New', Courier, monospace", textAlign: 'center' }}>
            Page {currentPage} of {totalPages}
          </div>
        )}

        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'center' }}>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '2px 6px',
                fontSize: '11px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: currentPage === 1 ? '#21262d' : '#3fb950',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: currentPage === 1 ? '#8b949e' : '#ffffff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                minWidth: '30px'
              }}
            >
              &lt;
            </button>

            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    style={{
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontFamily: "'Courier New', Courier, monospace",
                      backgroundColor: currentPage === pageNum ? '#3fb950' : '#21262d',
                      border: '1px solid #2a2d3a',
                      borderRadius: '3px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                      minWidth: '26px'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '2px 6px',
                fontSize: '11px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: currentPage === totalPages ? '#21262d' : '#3fb950',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: currentPage === totalPages ? '#8b949e' : '#ffffff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                minWidth: '30px'
              }}
            >
              &gt;
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Symbols;